/**
 * Writing Stats Page
 * Personal habit dashboard: streaks, lifetime totals, an 8-week
 * posts-per-week chart and badges. Everything is computed client-side
 * from the user's own posts (same fetch as the streak widget).
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthenticationContext';
import { computeWritingStreak } from '../utils/computeWritingStreak';
import { computeBadges } from '../utils/computeBadges';
import { computeBestStreak, computeWritingTotals, bucketPostsPerWeek } from '../utils/computeWritingStatistics';
import './WritingStatsPage.css';

const BAR_TRACK_PX = 120; // tallest bar height; plain CSS bars, no chart library

/** weekStart is YYYY-MM-DD (a Monday) — label as e.g. "29 Jun". */
const formatWeekLabel = (weekStart) => {
  const d = new Date(`${weekStart}T12:00:00Z`);
  return isNaN(d.getTime())
    ? weekStart
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const WritingStatsPage = () => {
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    if (!user?.id) return undefined;
    let active = true;
    axios
      .get(`/api/users/${user.id}`)
      .then((res) => {
        if (!active) return;
        setProfile(res.data.user);
        setPosts(res.data.posts || []);
      })
      .catch(() => { if (active) setPosts([]); });
    return () => { active = false; };
  }, [user?.id]);

  if (!token) {
    return (
      <div className="container">
        <h1>Your writing stats</h1>
        <div className="no-posts"><p><Link to="/login">Log in</Link> to see your writing stats.</p></div>
      </div>
    );
  }

  if (posts === null) {
    return <div className="container loading">Loading your stats...</div>;
  }

  const streak = computeWritingStreak(posts);
  const bestStreak = Math.max(computeBestStreak(posts), streak);
  const totals = computeWritingTotals(posts);
  const weeks = bucketPostsPerWeek(posts);
  const maxWeekCount = Math.max(1, ...weeks.map((w) => w.count));
  const badges = computeBadges({
    posts: totals.postsWritten,
    likesReceived: totals.likesReceived,
    followers: profile?.followers?.length || 0,
    streak,
    accountAgeDays: profile?.createdAt
      ? Math.floor((Date.now() - new Date(profile.createdAt)) / 86400000)
      : 0,
    isVerified: profile?.isVerified
  });

  const cards = [
    { label: 'Current streak', value: streak, suffix: streak === 1 ? 'day' : 'days' },
    { label: 'Best streak', value: bestStreak, suffix: bestStreak === 1 ? 'day' : 'days' },
    { label: 'Posts written', value: totals.postsWritten },
    { label: 'Words written', value: totals.wordsWritten.toLocaleString() },
    { label: 'Likes received', value: totals.likesReceived },
    { label: 'Comments received', value: totals.commentsReceived }
  ];

  return (
    <div className="container">
      <h1>Your writing stats</h1>

      <div className="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="stats-card">
            <span className="stats-value">
              {c.value}
              {c.suffix && <span className="stats-suffix"> {c.suffix}</span>}
            </span>
            <span className="stats-label">{c.label}</span>
          </div>
        ))}
      </div>

      <section className="stats-section">
        <h2>Posts per week</h2>
        <div className="stats-chart">
          {weeks.map((w) => (
            <div
              key={w.weekStart}
              className="stats-bar-col"
              title={`Week of ${formatWeekLabel(w.weekStart)}: ${w.count} post${w.count === 1 ? '' : 's'}`}
            >
              <span className="stats-bar-count">{w.count > 0 ? w.count : ''}</span>
              <div
                className={`stats-bar${w.count === 0 ? ' empty' : ''}`}
                style={{ height: `${Math.max(3, Math.round((w.count / maxWeekCount) * BAR_TRACK_PX))}px` }}
              />
              <span className="stats-bar-label">{formatWeekLabel(w.weekStart)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="stats-section">
        <h2>Badges</h2>
        {badges.length === 0 ? (
          <p className="stats-empty">No badges yet — they unlock as you write, get liked and keep streaks.</p>
        ) : (
          <div className="profile-badges">
            {badges.map((b) => (
              <span key={b.key} className="badge-chip" title={b.title}>
                <span className="badge-emoji">{b.emoji}</span> {b.label}
              </span>
            ))}
          </div>
        )}
      </section>

      {totals.postsWritten === 0 && (
        <div className="no-posts">
          <p>
            Nothing to count yet. <Link to="/?compose=1">Respond to today's prompt</Link> to
            start your streak.
          </p>
        </div>
      )}
    </div>
  );
};

export default WritingStatsPage;
